import {gql} from '@apollo/client';
import {Kind} from 'graphql';

import type {
  DocumentNode,
  FragmentDefinitionNode,
  InlineFragmentNode,
  SelectionNode,
} from 'graphql';

// Matches Apollo's graphql-tag normalization for fragment source deduplication.
function normalizeSource(source: string): string {
  return source.replace(/[\s,]+/g, ' ').trim();
}

function fragmentSourceKey(def: FragmentDefinitionNode): string {
  if (def.loc) {
    return normalizeSource(
      def.loc.source.body.substring(def.loc.start, def.loc.end),
    );
  }
  return def.name.value;
}

function isDocumentNode(value: unknown): value is DocumentNode {
  return (
    value !== null &&
    typeof value === 'object' &&
    'kind' in value &&
    'definitions' in value
  );
}

function getFirstFragmentDefinition(
  doc: DocumentNode,
): FragmentDefinitionNode | undefined {
  return doc.definitions.find(
    (def): def is FragmentDefinitionNode =>
      def.kind === Kind.FRAGMENT_DEFINITION,
  );
}

// Walks the selection tree and replaces placeholder FragmentSpread nodes
// (e.g. `...__InlineFragmentPlaceholder_0`) with InlineFragmentNodes built
// from the child document's first fragment definition. This preserves
// directives like @argumentDefinitions on the resulting inline fragment.
// If the child document contains additional fragment definitions (e.g. a
// shared data fragment), those are collected into auxiliaryDefs so they can
// be appended to the parent document.
function replaceSpreadSelections(
  selections: readonly SelectionNode[],
  inlineMap: Map<string, DocumentNode>,
  auxiliaryDefs: FragmentDefinitionNode[],
): SelectionNode[] {
  return selections.map((selection) => {
    if (
      selection.kind === Kind.FRAGMENT_SPREAD &&
      inlineMap.has(selection.name.value)
    ) {
      const childDoc = inlineMap.get(selection.name.value)!;
      const mainFrag = getFirstFragmentDefinition(childDoc);
      if (!mainFrag) {
        return selection;
      }

      for (const def of childDoc.definitions) {
        if (def.kind === Kind.FRAGMENT_DEFINITION && def !== mainFrag) {
          auxiliaryDefs.push(def);
        }
      }

      const inlineFragment: InlineFragmentNode = {
        kind: Kind.INLINE_FRAGMENT,
        typeCondition: mainFrag.typeCondition,
        directives: mainFrag.directives ?? [],
        selectionSet: mainFrag.selectionSet,
      };

      return inlineFragment;
    }

    // Recurse into fields and inline fragments that have nested selections
    if ('selectionSet' in selection && selection.selectionSet) {
      return {
        ...selection,
        selectionSet: {
          ...selection.selectionSet,
          selections: replaceSpreadSelections(
            selection.selectionSet.selections,
            inlineMap,
            auxiliaryDefs,
          ),
        },
      };
    }

    return selection;
  });
}

// Returns a lazy DocumentNode that defers compilation until a property is
// first accessed. This avoids parsing overhead at module load time — the
// gql call only happens when the document is actually used.
export default function tag(...args: Parameters<typeof gql>): DocumentNode {
  let compiled: DocumentNode | null = null;

  const [strings, ...expressions] = args as [
    TemplateStringsArray,
    ...unknown[],
  ];

  return {
    get kind() {
      if (!compiled) {
        compiled = compile(strings, expressions);
      }

      return compiled.kind;
    },

    get definitions() {
      if (!compiled) {
        compiled = compile(strings, expressions);
      }

      return compiled.definitions;
    },

    get loc() {
      if (!compiled) {
        compiled = compile(strings, expressions);
      }

      return compiled.loc;
    },
  };
}

// Apollo's gql concatenates a DocumentNode interpolation's source text directly,
// so `...${documentNode}` would produce `...fragment Foo on Type { ... }` which
// is invalid GraphQL. To support `...${doc}` syntax, we replace DocumentNode
// interpolations preceded by `...` with unique placeholder strings. Apollo then
// parses `...__InlineFragmentPlaceholder_N` as valid FragmentSpread nodes, which we
// swap in the post-processing step with InlineFragmentNodes built from the
// child document's fragment definition.
function compile(
  strings: TemplateStringsArray,
  expressions: unknown[],
): DocumentNode {
  const inlineMap = new Map<string, DocumentNode>();

  const processedExpressions = expressions.map((expr, index) => {
    const precedingString = strings[index];

    if (precedingString.trimEnd().endsWith('...') && isDocumentNode(expr)) {
      const placeholder = `__InlineFragmentPlaceholder_${index}`;
      inlineMap.set(placeholder, expr);
      return placeholder;
    }
    return expr;
  });

  const rawDoc = gql(strings, ...processedExpressions);

  if (inlineMap.size === 0) {
    return rawDoc;
  }

  const auxiliaryDefs: FragmentDefinitionNode[] = [];

  const newDefinitions = rawDoc.definitions.map((def) => {
    if (!('selectionSet' in def) || !def.selectionSet) {
      return def;
    }

    return {
      ...def,
      selectionSet: {
        ...def.selectionSet,
        selections: replaceSpreadSelections(
          def.selectionSet.selections,
          inlineMap,
          auxiliaryDefs,
        ),
      },
    };
  });

  // Deduplicate auxiliary fragments following Apollo's graphql-tag approach:
  // dedup by normalized source content, warn on name collisions with
  // different source text.
  const seenKeys = new Set<string>();
  const nameToKey = new Map<string, string>();

  for (const def of newDefinitions) {
    if (def.kind === Kind.FRAGMENT_DEFINITION) {
      const key = fragmentSourceKey(def as FragmentDefinitionNode);
      seenKeys.add(key);
      nameToKey.set((def as FragmentDefinitionNode).name.value, key);
    }
  }

  const dedupedAux = auxiliaryDefs.filter((def) => {
    const name = def.name.value;
    const key = fragmentSourceKey(def);

    const existingKey = nameToKey.get(name);

    if (existingKey != null && existingKey !== key) {
      // eslint-disable-next-line no-console
      console.warn(`Warning: fragment with name ${name} already exists.\n`);
    }

    if (seenKeys.has(key)) {
      return false;
    }

    seenKeys.add(key);
    nameToKey.set(name, key);

    return true;
  });

  return {...rawDoc, definitions: [...newDefinitions, ...dedupedAux]};
}
